import type { Disposable } from "@/shared/contracts/lifecycle";
import type { HoverIdleSnapshot } from "../types";
import { getMessages } from "@/i18n/runtime";
import { formatForContext } from "./numberFormat";
import { huge, hugeZero, type HugeNumberInput } from "@/shared/numbers";
import { HoverIntentController } from "@/features/hoverIdle/core/hoverIntentController";
import type { HoverPointerModel } from "../input/hoverPointerModel";
import { HOV_O_LABEL_HOVER_TARGET_SECONDS } from "@/features/hoverIdle/core/hoverIdleModel";

// Match MP slot hover-charge timing so TimHov activation toggles share the
// same operational feel as MP placement/retrieval.
const TIM_HOV_ACTIVATION_HOVER_CHARGE_MS = 2000;

export interface ResourceHudViewOptions {
  readonly pointerModel: HoverPointerModel;
  readonly onHovOLabelHoverChange: (isHovering: boolean) => void;
  readonly onTimHovActivationToggle: () => void;
  // Resolved at charge-start so the user's "hover toggle duration" setting
  // applies to the next hover without rebuilding the view.
  readonly getHoverToggleDurationMs?: () => number;
}

export interface ResourceHudUpdateInputs {
  readonly snapshot: HoverIdleSnapshot;
  readonly recentHovRatePerSecond?: HugeNumberInput;
  readonly hovOLabelUnlocked?: boolean;
  readonly timHovActivationUnlocked?: boolean;
}

export class ResourceHudView implements Disposable {
  readonly root: HTMLElement;
  private readonly resourceLine: HTMLDivElement;
  private readonly recentRateLine: HTMLDivElement;
  private readonly hovGainLine: HTMLDivElement;
  private readonly timGainLine: HTMLDivElement;
  private readonly hovValueText: Text;
  private readonly hovOLetter: HTMLSpanElement;
  private readonly timLeadingText: Text;
  private readonly timHovActivationToggle: HTMLButtonElement;
  private readonly timHovActivationTrailingText: Text;
  private readonly timValueText: Text;
  private readonly timSuffixText: Text;
  private readonly faiText: Text;
  private readonly hovOIntent: HoverIntentController;
  private readonly timHovActivationIntent: HoverIntentController;
  private readonly onTimHovActivationToggle: () => void;
  private readonly getHoverToggleDurationMs: (() => number) | null;
  private timHovActivationChargeTimer: ReturnType<typeof setTimeout> | null =
    null;
  private isTimHovActivationActive = true;

  constructor(options: ResourceHudViewOptions) {
    const messages = getMessages();
    this.root = document.createElement("section");
    this.root.className = "hover-idle-hud-card hover-idle-hud";
    this.root.setAttribute("aria-label", messages.resourceHud.ariaLabel);

    this.resourceLine = document.createElement("div");
    this.resourceLine.dataset.resourceLine = "resources";

    const hovWord = document.createElement("span");
    hovWord.className = "hov hover-idle-hud-card__primary";
    hovWord.setAttribute("aria-label", messages.resourceHud.hovWordAriaLabel);

    this.hovValueText = document.createTextNode("");
    const hovValueSuffixText = document.createTextNode(
      messages.resourceHud.hovValueSuffix,
    );
    const hovWordHText = document.createTextNode(messages.resourceHud.hovWordH);
    const hovWordVText = document.createTextNode(messages.resourceHud.hovWordV);

    this.hovOLetter = document.createElement("span");
    this.hovOLetter.className = "hov-o";
    this.hovOLetter.dataset.unlocked = "false";
    this.hovOLetter.tabIndex = 0;
    this.hovOLetter.setAttribute("role", "button");
    this.hovOLetter.setAttribute(
      "aria-label",
      messages.resourceHud.hovOAriaLabel,
    );
    this.hovOLetter.style.setProperty("--hov-o-fill-progress", "0%");

    const hovOFill = document.createElement("span");
    hovOFill.className = "hov-o-fill";
    hovOFill.setAttribute("aria-hidden", "true");

    const hovOText = document.createElement("span");
    hovOText.className = "hov-o-text";
    hovOText.appendChild(
      document.createTextNode(messages.resourceHud.hovWordO),
    );

    this.hovOLetter.append(hovOFill, hovOText);

    hovWord.append(
      this.hovValueText,
      hovValueSuffixText,
      hovWordHText,
      this.hovOLetter,
      hovWordVText,
    );

    this.timLeadingText = document.createTextNode(
      messages.resourceHud.timSeparator,
    );
    this.timHovActivationToggle = document.createElement("button");
    this.timHovActivationToggle.type = "button";
    this.timHovActivationToggle.className =
      "hover-idle-tim-hov-activation-toggle";
    this.timHovActivationToggle.dataset.state = "filled";
    this.timHovActivationToggle.hidden = true;
    this.timHovActivationToggle.setAttribute(
      "aria-label",
      messages.resourceHud.timHovActivationAriaLabel,
    );
    this.timHovActivationToggle.setAttribute("aria-pressed", "true");

    const timHovActivationFill = document.createElement("span");
    timHovActivationFill.className =
      "hover-idle-tim-hov-activation-toggle-fill";
    timHovActivationFill.setAttribute("aria-hidden", "true");

    const timHovActivationIcon = document.createElement("span");
    timHovActivationIcon.className =
      "hover-idle-tim-hov-activation-toggle-icon";
    timHovActivationIcon.setAttribute("aria-hidden", "true");
    timHovActivationIcon.innerHTML =
      '<svg viewBox="0 0 24 24" width="16" height="16" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="M10 5 L3 12 L10 19 M3 12 L20 12" ' +
      'fill="none" stroke="currentColor" stroke-width="2.4" ' +
      'stroke-linecap="round" stroke-linejoin="round"/>' +
      "</svg>";
    this.timHovActivationToggle.append(
      timHovActivationFill,
      timHovActivationIcon,
    );
    this.timHovActivationTrailingText = document.createTextNode("");
    this.timValueText = document.createTextNode("");
    this.timSuffixText = document.createTextNode(
      messages.resourceHud.timSuffix,
    );
    this.faiText = document.createTextNode("");

    this.resourceLine.append(
      hovWord,
      this.timLeadingText,
      this.timHovActivationToggle,
      this.timHovActivationTrailingText,
      this.timValueText,
      this.timSuffixText,
      this.faiText,
    );

    this.recentRateLine = document.createElement("div");
    this.recentRateLine.className = "hover-idle-hud-card__secondary";
    this.recentRateLine.dataset.resourceLine = "recent-rate";
    this.hovGainLine = document.createElement("div");
    this.hovGainLine.className = "hover-idle-hud-card__secondary";
    this.hovGainLine.dataset.resourceLine = "hov-gain";
    this.timGainLine = document.createElement("div");
    this.timGainLine.className = "hover-idle-hud-card__secondary";
    this.timGainLine.dataset.resourceLine = "tim-gain";
    this.root.append(
      this.resourceLine,
      this.recentRateLine,
      this.hovGainLine,
      this.timGainLine,
    );

    this.hovOIntent = new HoverIntentController({
      target: this.hovOLetter,
      pointerModel: options.pointerModel,
      onHoverChange: options.onHovOLabelHoverChange,
    });
    this.onTimHovActivationToggle = options.onTimHovActivationToggle;
    this.getHoverToggleDurationMs = options.getHoverToggleDurationMs ?? null;
    this.timHovActivationIntent = new HoverIntentController({
      target: this.timHovActivationToggle,
      pointerModel: options.pointerModel,
      onHoverChange: (isHovering) =>
        this.handleTimHovActivationHoverChange(isHovering),
    });
  }

  private handleTimHovActivationHoverChange(isHovering: boolean): void {
    if (!isHovering) {
      this.cancelTimHovActivationCharge();
      return;
    }
    if (this.timHovActivationToggle.hidden) {
      return;
    }
    if (this.timHovActivationChargeTimer !== null) {
      return;
    }
    const chargeMs =
      this.getHoverToggleDurationMs?.() ?? TIM_HOV_ACTIVATION_HOVER_CHARGE_MS;
    this.timHovActivationToggle.style.setProperty(
      "--hover-toggle-charge-duration",
      `${chargeMs}ms`,
    );
    this.timHovActivationToggle.dataset.state = this.isTimHovActivationActive
      ? "retrieving"
      : "charging";
    this.timHovActivationChargeTimer = setTimeout(() => {
      this.timHovActivationChargeTimer = null;
      this.onTimHovActivationToggle();
    }, chargeMs);
  }

  private cancelTimHovActivationCharge(): void {
    if (this.timHovActivationChargeTimer !== null) {
      clearTimeout(this.timHovActivationChargeTimer);
      this.timHovActivationChargeTimer = null;
    }
    this.timHovActivationToggle.dataset.state = this.isTimHovActivationActive
      ? "filled"
      : "empty";
  }

  update(
    snapshot: HoverIdleSnapshot,
    recentHovRatePerSecond: HugeNumberInput = hugeZero(),
    hovOLabelUnlocked = false,
    timHovActivationUnlocked = false,
  ): void {
    const messages = getMessages();
    this.hovValueText.data = formatForContext(snapshot.hov, "main");
    this.timValueText.data = formatForContext(snapshot.tim, "main");
    this.faiText.data =
      snapshot.fai > 0
        ? messages.resourceHud.fai(formatForContext(snapshot.fai, "main"))
        : "";

    this.recentRateLine.textContent = messages.resourceHud.recentRate(
      formatForContext(huge(recentHovRatePerSecond), "compact"),
    );
    this.hovGainLine.textContent = messages.resourceHud.hovGain(
      formatForContext(snapshot.hovGainPerSecond, "compact"),
    );
    this.timGainLine.textContent = snapshot.timGainPerSecond.gt(0.01)
      ? messages.resourceHud.timGain(
          formatForContext(snapshot.timGainPerSecond, "compact"),
        )
      : "";

    const ratio = hovOLabelUnlocked
      ? 1
      : Math.max(
          0,
          Math.min(
            1,
            snapshot.hovOLabelHoverSeconds / HOV_O_LABEL_HOVER_TARGET_SECONDS,
          ),
        );
    this.hovOLetter.dataset.unlocked = hovOLabelUnlocked ? "true" : "false";
    this.hovOLetter.style.setProperty(
      "--hov-o-fill-progress",
      `${ratio * 100}%`,
    );

    const isTimHovActivationEnabled = snapshot.isTimHovActivationEnabled;
    this.timLeadingText.data = timHovActivationUnlocked
      ? " "
      : messages.resourceHud.timSeparator;
    this.timHovActivationTrailingText.data = timHovActivationUnlocked
      ? " "
      : "";
    this.timHovActivationToggle.hidden = !timHovActivationUnlocked;
    if (this.isTimHovActivationActive !== isTimHovActivationEnabled) {
      this.cancelTimHovActivationCharge();
      this.isTimHovActivationActive = isTimHovActivationEnabled;
    }
    if (this.timHovActivationChargeTimer === null) {
      this.timHovActivationToggle.dataset.state = isTimHovActivationEnabled
        ? "filled"
        : "empty";
    }
    this.timHovActivationToggle.setAttribute(
      "aria-pressed",
      isTimHovActivationEnabled ? "true" : "false",
    );
  }

  dispose(): void {
    this.cancelTimHovActivationCharge();
    this.timHovActivationIntent.dispose();
    this.hovOIntent.dispose();
    this.root.remove();
  }
}
